/**
 * @lodex-pro/shared/jobs-paging — the page-size caps for the two job list
 * endpoints, defined ONCE so no caller has to retype them.
 *
 * ── Why this module exists (OPEN-056) ───────────────────────────────────────
 * There are two job-list endpoints with DIFFERENT caps, and the difference is
 * deliberate but was invisible:
 *
 *   GET /v1/jobs            -> 100   heavy rows (each hydrates captures + tanks)
 *   GET /v1/jobs/completed  -> 200   lean rows, built for a table and CSV export
 *
 * Nothing at either definition mentioned the other, and the numbers were magic
 * literals retyped across layers — the server schema, and three separate places
 * in the CSV export loop. So `limit=200` read as obviously fine, because it IS
 * fine on the completed endpoint. Used against the general list it returned
 * `400 { path: 'limit', reason: 'too_big' }`, and OperationsPage fed that
 * through `?? []`, pinning the COMPLETED TODAY tile to 0 with nothing on screen
 * to indicate a failure. That was OPEN-055.
 *
 * ── Why the caps are NOT simply equalised ───────────────────────────────────
 * The obvious "fix the mismatch" is to make both the same number. That trades
 * one problem for another, in whichever direction it is taken:
 *
 *   raise /v1/jobs to 200      -> doubles the worst-case payload of the HEAVY
 *                                 query, which is what the 100 is protecting
 *   lower /v1/jobs/completed   -> doubles the number of requests the CSV export
 *     to 100                      makes, for no benefit to anyone
 *
 * The caps differ because the payloads differ. The defect was never that the
 * numbers disagree — it was that they were anonymous, duplicated, and silent
 * about each other. Naming them here fixes that without pretending two
 * different queries have the same cost.
 */

/**
 * Max `limit` accepted by `GET /v1/jobs`.
 *
 * Lower than {@link COMPLETED_JOBS_MAX_LIMIT} on purpose: this endpoint returns
 * fully hydrated rows (haul captures, per-tank readings), so a page costs far
 * more to build and transfer.
 */
export const JOBS_LIST_MAX_LIMIT = 100;

/**
 * Max `limit` accepted by `GET /v1/jobs/completed`.
 *
 * Higher than {@link JOBS_LIST_MAX_LIMIT} on purpose: the completed view
 * returns lean rows and is paged by the CSV export, where fewer round-trips is
 * the point.
 */
export const COMPLETED_JOBS_MAX_LIMIT = 200;
